import React, { useContext, useState, useEffect } from 'react';
import { Controller } from 'react-hook-form';


import { Stack } from 'ui/stack';

import Input from 'components/partials/input/input';
import Select from 'components/partials/select/select';

import { UserProfileContext } from 'containers/admin/users/user/user-profile/user-profile';

import { useAllClients } from 'hooks/api/use-clients';
import type { ClientRecord } from 'hooks/api/use-clients';

import {
  mapClientsToSelectOptions,
  mapRolesToSelectOptions,
} from 'helpers/forms';
import type { SelectOption } from 'helpers/forms';

import { PERSONAL_INFO_FORM_ALWAYS_EDITABLE_FIELDS } from 'containers/admin/users/user/forms/personal-info-form/personal-info-form-fields.constants';
import {
  USER_DETAILS_FORM_FIELDS,
  USER_DETAILS_FIELD_NAMES,
} from 'containers/admin/users/user/forms/user-details-form/user-details-form-fields.constants';
import type {
  UserDetailsFormField,
  UserDetailsFormFieldMode,
} from 'containers/admin/users/user/forms/user-details-form/user-details-form-fields.constants';
import { USER_ROLES_FORM_FIELDS } from 'containers/admin/users/user/forms/user-details-form/user-role-form-fields.constants.';
import { useReadOnly } from 'hooks/read-only';

interface UserDetailsFormProps {
  currentUserRole?: string;
  currentClient?: ClientRecord | null;
  isNotNewUser?: boolean;
  isFormDisabled?: boolean;
  isSelectDisabled?: boolean;
  isMVUser?: boolean;
  isCurrentUserProfile?: boolean;
}

interface FieldLike {
  onChange: (value: unknown) => void;
}

const UserDetailsForm = ({
  currentUserRole,
  currentClient,
  isNotNewUser,
  isFormDisabled,
  isSelectDisabled,
  isMVUser,
  isCurrentUserProfile = false,
}: UserDetailsFormProps): React.JSX.Element => {
  const { control, errors, clearErrors, resetField, canOnlyEditJobTitle } =
    useContext(UserProfileContext);

  const [isSelectVisible, setIsSelectVisible] = useState(false);
  const [allClients, setAllClients] = useState<ClientRecord[]>([]);
  const [showHelperText, setShowHelperText] = useState(true);
  const [currentSelectedRole, setCurrentSelectedRole] =
    useState<SelectOption | null>(null);
  const [currentSelectedClient, setCurrentSelectedClient] =
    useState<SelectOption | null>(null);
  const [currentAuthorizedDomains, setCurrentAuthorizedDomains] = useState(
    '@domain.com, @domain.net.'
  );
  const [userDetailsFields, setUserDetailsFields] = useState<
    UserDetailsFormField[]
  >(USER_DETAILS_FORM_FIELDS);

  const { data: allClientsData } = useAllClients();

  const readOnly = useReadOnly();

  const domainText: string =
    (currentClient?.authorizedDomains as string | undefined) ||
    currentAuthorizedDomains;
  const isRoleSelectDisabled =
    isFormDisabled || isCurrentUserProfile || canOnlyEditJobTitle;

  const isSideBySide = !isFormDisabled && !isNotNewUser;

  const filterFormFieldsByMode = (
    filterKey: UserDetailsFormFieldMode
  ): UserDetailsFormField[] =>
    USER_DETAILS_FORM_FIELDS.filter((field) => field.mode === filterKey);

  const calculateFormFieldDisability = (
    fields: UserDetailsFormField[]
  ): UserDetailsFormField[] =>
    fields.map((field) => {
      const disabled =
        !PERSONAL_INFO_FORM_ALWAYS_EDITABLE_FIELDS.has(field.name as never) &&
        readOnly;

      return { ...field, disabled };
    });

  const addOptions = (
    label: string
  ): (SelectOption | undefined)[] | undefined => {
    switch (label) {
      case 'Role':
        return mapRolesToSelectOptions(USER_ROLES_FORM_FIELDS);

      case 'Client':
        return mapClientsToSelectOptions(allClients as never);

      default:
        return undefined;
    }
  };

  const customOnChange = (
    label: string,
    field: FieldLike
  ): ((item: SelectOption) => void) | undefined => {
    switch (label) {
      case 'Role':
        return (item: SelectOption) => {
          setCurrentSelectedRole(item);

          if (item.value !== 'user' && item.value !== 'internalUser') {
            setUserDetailsFields(
              userDetailsFields.filter(
                (elem) => elem.name !== USER_DETAILS_FIELD_NAMES.CLIENT
              ) as UserDetailsFormField[]
            );

            setShowHelperText(false);
            setCurrentSelectedClient(null);
          } else {
            setShowHelperText(true);
            setUserDetailsFields((prev) => {
              const hasClient = prev.some(
                (f) => f.name === USER_DETAILS_FIELD_NAMES.CLIENT
              );

              if (hasClient) return prev;

              const clientField = USER_DETAILS_FORM_FIELDS.find(
                (f) => f.name === USER_DETAILS_FIELD_NAMES.CLIENT
              );

              return clientField ? [...prev, clientField] : prev;
            });
          }

          field.onChange(item.value);
        };

      case 'Client':
        return (item: SelectOption) => {
          if (item) setCurrentSelectedClient(item);

          field.onChange(item);
        };

      default:
        return undefined;
    }
  };

  useEffect(() => {
    if (!isNotNewUser) {
      return;
    }

    const fields = calculateFormFieldDisability(
      filterFormFieldsByMode('CREATE_AND_EDIT_MODE')
    );

    setUserDetailsFields(fields);
  }, [isNotNewUser]);

  useEffect(() => {
    const matchingRole = USER_ROLES_FORM_FIELDS.find(
      (role) =>
        role.label === currentUserRole || role.radioValue === currentUserRole
    );

    if (matchingRole) {
      setCurrentSelectedRole({
        label: matchingRole.label,
        value: matchingRole.radioValue,
      });
    }
  }, [currentUserRole]);

  useEffect(() => {
    if (!currentSelectedRole) {
      return;
    }

    const { value } = currentSelectedRole;

    const selectIsDisplayed = value === 'user' || value === 'internalUser';

    if (!selectIsDisplayed) clearErrors(USER_DETAILS_FIELD_NAMES.CLIENT);

    if (!selectIsDisplayed) resetField(USER_DETAILS_FIELD_NAMES.CLIENT);

    clearErrors(USER_DETAILS_FIELD_NAMES.EMAIL);

    setIsSelectVisible(selectIsDisplayed);
  }, [currentSelectedRole, currentSelectedClient]);

  useEffect(() => {
    if (!isSelectDisabled && allClientsData.length > 0) {
      setAllClients(allClientsData);
    }
  }, [isSelectDisabled, allClientsData]);

  useEffect(() => {
    const selectedClient = allClients.find(
      (client) => client.id === currentSelectedClient?.value
    );

    if (selectedClient && typeof selectedClient.authorizedDomains === 'string')
      setCurrentAuthorizedDomains(selectedClient.authorizedDomains);
  }, [currentSelectedClient]);

  return (
    <Stack
      sx={[
        !!isFormDisabled && { width: '100%' },
        isSideBySide && { width: '50%' },
      ]}
    >
      {userDetailsFields.map(
        ({
          label,
          name,
          key,
          isSelect,
          renderComponentProps,
          disabled,
          alwaysVisible,
        }) => (
          <Controller
            key={key}
            name={name}
            control={control as never}
            render={({ field }) =>
              isSelect ? (
                (((isSelectVisible || alwaysVisible) && (
                  <Select
                    customClass="w-36"
                    fieldError={errors[name]}
                    label={label}
                    labelInTop
                    clearInputErr={() => clearErrors(name)}
                    labelFor={name}
                    errors={errors}
                    disabledBgColor={
                      ((name === USER_DETAILS_FIELD_NAMES.ROLE
                        ? isRoleSelectDisabled
                        : isSelectDisabled ||
                          isFormDisabled ||
                          canOnlyEditJobTitle) &&
                        '#F2F2F2') ||
                      undefined
                    }
                    disabled={
                      name === USER_DETAILS_FIELD_NAMES.ROLE
                        ? isRoleSelectDisabled
                        : isSelectDisabled ||
                          isFormDisabled ||
                          disabled ||
                          canOnlyEditJobTitle
                    }
                    options={
                      (name === USER_DETAILS_FIELD_NAMES.ROLE
                        ? currentClient
                          ? [
                              { label: 'Campus User', value: 'user' },
                              {
                                label: 'Internal User',
                                value: 'internalUser',
                              },
                            ]
                          : addOptions(label)
                        : addOptions(label)) as never
                    }
                    required={
                      isMVUser
                        ? !isSelectDisabled
                        : isSelectDisabled
                        ? isFormDisabled && disabled
                        : !isFormDisabled
                    }
                    {...renderComponentProps}
                    {...field}
                    onChange={(option) => {
                      const handler = customOnChange(label, field);

                      if (handler && option && !Array.isArray(option)) {
                        handler(option);
                      }
                    }}
                  />
                )) as React.ReactElement)
              ) : (
                <Input
                  customClass="w-36"
                  errorsNames={errors[name]}
                  domainText={
                    name === USER_DETAILS_FIELD_NAMES.EMAIL ? domainText : ''
                  }
                  label={label}
                  labelFor={name}
                  labelInTop
                  inputWrapperCustomClass={'bg-gray-100'}
                  disabled={isFormDisabled || disabled || canOnlyEditJobTitle}
                  disabledClassName={
                    ((isFormDisabled || readOnly) && 'bg-gray-100') || undefined
                  }
                  required={isMVUser ? !isSelectDisabled : !isFormDisabled}
                  {...field}
                  {...renderComponentProps}
                  helperText={
                    name === USER_DETAILS_FIELD_NAMES.EMAIL
                      ? showHelperText &&
                        (currentSelectedClient || currentClient)
                        ? renderComponentProps.helperText
                        : ''
                      : renderComponentProps.helperText
                  }
                />
              )
            }
          />
        )
      )}
    </Stack>
  );
};

export default UserDetailsForm;
